import { Check, Loader2, Share2 } from 'lucide-react';
import { useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isShareCancelled, shareOrCopy } from '../lib/share';
import { useToast } from '../store/hooks';

type ShareButtonVariant = 'icon' | 'primary';

interface ShareButtonProps {
  getText: () => Promise<string> | string;
  title?: string;
  ariaLabel?: string;
  className?: string;
  iconSize?: number;
  variant?: ShareButtonVariant;
  label?: string;
  successLabel?: string;
  onError?: (error: unknown) => void;
}

const ShareButton = ({
  getText,
  title,
  ariaLabel,
  className,
  iconSize = 18,
  variant = 'icon',
  label,
  successLabel,
  onError,
}: ShareButtonProps) => {
  const { t } = useTranslation();
  const { showSuccess } = useToast();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [outcome, setOutcome] = useState<'shared' | 'copied' | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimer = () => {
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }
  };

  const markSuccess = (next: 'shared' | 'copied') => {
    setOutcome(next);
    setStatus('success');
    clearResetTimer();
    resetTimer.current = setTimeout(() => {
      setStatus('idle');
      setOutcome(null);
    }, 2000);
  };

  const handleClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (status === 'loading') return;

    clearResetTimer();
    setStatus('loading');
    setOutcome(null);

    try {
      const text = await getText();
      const result = await shareOrCopy(text, title);
      if (result === 'copied') {
        showSuccess(t('Скопировано в буфер обмена'));
      }
      markSuccess(result);
    } catch (error) {
      if (isShareCancelled(error)) {
        setStatus('idle');
        return;
      }
      setStatus('idle');
      onError?.(error);
    }
  };

  const resolvedAriaLabel = ariaLabel ?? t('Поделиться');
  const resolvedLabel = label ?? t('Поделиться');
  const resolvedSuccessLabel =
    outcome === 'copied'
      ? t('Скопировано')
      : (successLabel ?? t('Отправлено'));

  const icon =
    status === 'loading' ? (
      <Loader2 size={iconSize} className="animate-spin" />
    ) : status === 'success' ? (
      <Check size={iconSize} className="text-emerald-600" />
    ) : (
      <Share2 size={iconSize} />
    );

  if (variant === 'primary') {
    return (
      <button
        type="button"
        className={className ?? 'btn-primary w-full'}
        onClick={(e) => void handleClick(e)}
        disabled={status === 'loading'}
        aria-label={resolvedAriaLabel}
      >
        {icon}
        <span>{status === 'success' ? resolvedSuccessLabel : resolvedLabel}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={
        className ??
        'p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary disabled:opacity-50'
      }
      onClick={(e) => void handleClick(e)}
      disabled={status === 'loading'}
      aria-label={resolvedAriaLabel}
      title={resolvedAriaLabel}
    >
      {icon}
    </button>
  );
};

export default ShareButton;
